// @ts-nocheck
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import TeamSearchDialog, { TeamSearchMatch } from '@/components/teams/TeamSearchDialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, AlertCircle, CheckCircle2, X } from 'lucide-react';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

function useTeamCounts(teamId?: string) {
  return useQuery({
    enabled: !!teamId,
    queryKey: ['team-merge-counts', teamId],
    queryFn: async () => {
      const [regs, rosters, members] = await Promise.all([
        (supabase as any).from('event_registrations_v2').select('*', { count: 'exact', head: true }).eq('team_id', teamId),
        (supabase as any).from('event_rosters').select('*', { count: 'exact', head: true }).eq('team_id', teamId),
        (supabase as any).from('team_members').select('*', { count: 'exact', head: true }).eq('team_id', teamId),
      ]);
      return {
        regs: regs.count ?? 0,
        rosters: rosters.count ?? 0,
        members: members.count ?? 0,
      };
    },
  });
}

function TeamPickerCard({
  label,
  team,
  onSelect,
  onClear,
}: {
  label: string;
  team: TeamSearchMatch | null;
  onSelect: (t: TeamSearchMatch) => void;
  onClear: () => void;
}) {
  const counts = useTeamCounts(team?.team_id);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{label}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {team ? (
          <div className="space-y-3">
            <div className="rounded-md border p-3 space-y-1.5">
              <div className="flex items-start justify-between gap-2">
                <div className="font-semibold">{team.canonical_name}</div>
                <Button variant="ghost" size="sm" onClick={onClear}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {team.age_group && <Badge variant="secondary">{team.age_group}</Badge>}
                {team.gender && <Badge variant="secondary">{team.gender}</Badge>}
                {team.home_state && <Badge variant="outline">{team.home_state}</Badge>}
                {team.club_name && <Badge variant="outline">{team.club_name}</Badge>}
              </div>
              {team.primary_coach_name && (
                <div className="text-xs text-muted-foreground">
                  Coach: {team.primary_coach_name}
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-md bg-muted/40 p-2">
                <div className="text-lg font-bold">{counts.data?.members ?? '–'}</div>
                <div className="text-[10px] text-muted-foreground uppercase">Members</div>
              </div>
              <div className="rounded-md bg-muted/40 p-2">
                <div className="text-lg font-bold">{counts.data?.regs ?? '–'}</div>
                <div className="text-[10px] text-muted-foreground uppercase">Registrations</div>
              </div>
              <div className="rounded-md bg-muted/40 p-2">
                <div className="text-lg font-bold">{counts.data?.rosters ?? '–'}</div>
                <div className="text-[10px] text-muted-foreground uppercase">Roster spots</div>
              </div>
            </div>
          </div>
        ) : (
          <TeamSearchDialog
            open
            embedded
            organizationId={undefined as any}
            onOpenChange={() => {}}
            onTeamSelected={onSelect}
            onCreateNew={() => {}}
            onJoinRequested={() => {}}
          />
        )}
      </CardContent>
    </Card>
  );
}

export default function TeamMergeTool() {
  const navigate = useNavigate();
  const [source, setSource] = useState<TeamSearchMatch | null>(null);
  const [target, setTarget] = useState<TeamSearchMatch | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [merging, setMerging] = useState(false);
  const [completed, setCompleted] = useState<{ count: number } | null>(null);

  const sameTeam = source && target && source.team_id === target.team_id;
  const canPreview = !!source && !!target && !sameTeam;

  const handlePreview = async () => {
    if (!source || !target) return;
    setPreviewing(true);
    setPreview(null);
    try {
      const { data, error } = await invokeEdgeFunction('rle-merge-teams', {
        body: {
          source_team_id: source.team_id,
          target_team_id: target.team_id,
          reason: '(preview)',
          preview: true,
        },
      });
      if (error) throw error;
      setPreview(data?.data ?? data);
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to preview merge');
    } finally {
      setPreviewing(false);
    }
  };

  const handleConfirmMerge = async () => {
    if (!source || !target || !reason.trim() || confirmText !== 'MERGE') return;
    setMerging(true);
    try {
      const { data, error } = await invokeEdgeFunction('rle-merge-teams', {
        body: {
          source_team_id: source.team_id,
          target_team_id: target.team_id,
          reason: reason.trim(),
          preview: false,
        },
      });
      if (error) throw error;
      const result = data?.data ?? data ?? {};
      const total =
        (result.event_registrations_reassigned ?? 0) +
        (result.event_rosters_reassigned ?? 0) +
        (result.team_members_reassigned ?? 0) +
        (result.player_history_reassigned ?? 0);
      toast.success(`Merge complete — ${total} records reassigned`);
      setCompleted({ count: total });
      setConfirmOpen(false);
      setTimeout(() => {
        navigate(`/teams/${target.stable_slug}`);
      }, 1200);
    } catch (e: any) {
      toast.error(e?.message ?? 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  const reset = () => {
    setSource(null);
    setTarget(null);
    setPreview(null);
    setReason('');
    setConfirmText('');
    setCompleted(null);
  };

  if (completed) {
    return (
      <div className="container max-w-3xl mx-auto py-12 px-4">
        <Card>
          <CardContent className="py-12 text-center space-y-4">
            <CheckCircle2 className="h-12 w-12 text-primary mx-auto" />
            <h2 className="text-xl font-semibold">Merge complete</h2>
            <p className="text-muted-foreground">
              {completed.count} records reassigned. Redirecting to target team...
            </p>
            <div className="flex justify-center gap-2 pt-2">
              <Button variant="outline" onClick={reset}>Merge another</Button>
              <Button onClick={() => navigate('/')}>Go home</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container max-w-5xl mx-auto py-8 px-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Merge duplicate teams</h1>
        <p className="text-sm text-muted-foreground">
          Combine two team records that should be a single team.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TeamPickerCard
          label="Source team (will be merged away)"
          team={source}
          onSelect={setSource}
          onClear={() => {
            setSource(null);
            setPreview(null);
          }}
        />
        <TeamPickerCard
          label="Target team (will remain)"
          team={target}
          onSelect={setTarget}
          onClear={() => {
            setTarget(null);
            setPreview(null);
          }}
        />
      </div>

      {sameTeam && (
        <Alert variant="destructive" className="mt-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Source and target must be different teams.</AlertDescription>
        </Alert>
      )}

      <div className="mt-6 flex justify-center">
        <Button
          size="lg"
          disabled={!canPreview || previewing}
          onClick={handlePreview}
        >
          {previewing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Preview merge
        </Button>
      </div>

      {preview && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-base">Merge preview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-md border p-3 text-center">
                <div className="text-2xl font-bold">{preview.event_registrations_reassigned ?? 0}</div>
                <div className="text-xs text-muted-foreground">Registrations</div>
              </div>
              <div className="rounded-md border p-3 text-center">
                <div className="text-2xl font-bold">{preview.event_rosters_reassigned ?? 0}</div>
                <div className="text-xs text-muted-foreground">Roster spots</div>
              </div>
              <div className="rounded-md border p-3 text-center">
                <div className="text-2xl font-bold">{preview.team_members_reassigned ?? 0}</div>
                <div className="text-xs text-muted-foreground">Members</div>
              </div>
              <div className="rounded-md border p-3 text-center">
                <div className="text-2xl font-bold">{preview.player_history_reassigned ?? 0}</div>
                <div className="text-xs text-muted-foreground">Player history</div>
              </div>
            </div>

            {Array.isArray(preview.aliases) && preview.aliases.length > 0 && (
              <div className="space-y-1.5">
                <div className="text-sm font-medium">Aliases to be added</div>
                <div className="flex flex-wrap gap-1.5">
                  {preview.aliases.map((a: string, i: number) => (
                    <Badge key={i} variant="secondary">{a}</Badge>
                  ))}
                </div>
              </div>
            )}

            {Array.isArray(preview.conflicts) && preview.conflicts.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Conflicts ({preview.conflicts.length})</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc pl-4 mt-2 space-y-1 text-sm">
                    {preview.conflicts.map((c: any, i: number) => (
                      <li key={i}>
                        <strong>{c.entity}:</strong> {c.detail}
                        {c.resolution && (
                          <span className="text-muted-foreground"> → {c.resolution}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <Separator />

            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="reason">
                Reason for merge <span className="text-destructive">*</span>
              </label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 500))}
                placeholder="Explain why these teams are being merged..."
                rows={3}
              />
              <div className="text-xs text-muted-foreground text-right">{reason.length}/500</div>
            </div>

            <Button
              variant="destructive"
              size="lg"
              className="w-full"
              disabled={!reason.trim()}
              onClick={() => {
                setConfirmText('');
                setConfirmOpen(true);
              }}
            >
              Confirm merge — this cannot be undone
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm merge</DialogTitle>
            <DialogDescription>
              You are about to merge <strong>{source?.canonical_name}</strong> into{' '}
              <strong>{target?.canonical_name}</strong>. This cannot be undone.
              Type <code className="bg-muted px-1 rounded">MERGE</code> to confirm.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type MERGE"
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={merging}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={confirmText !== 'MERGE' || merging}
              onClick={handleConfirmMerge}
            >
              {merging && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Merge teams
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
